import type { ReactNode, Dispatch, SetStateAction } from "react"
import { useState, useEffect, useRef } from "react"
import cn from "@/v1-ui/utils/utils.cn"
import Popover, { type PopoverProps } from "@/v1-ui/Popover"
import Button, { type ButtonProps } from "./Button"

type ConfirmButtonRenderer = (
  shown: boolean,
  setShown: Dispatch<SetStateAction<boolean>>
) => ReactNode

type BaseProps = Omit<(PopoverProps & ButtonProps), "content" | "children">
export type ConfirmButtonProps = BaseProps & {
  onConfirm?: ButtonProps["onClick"],
  onCancel?: ButtonProps["onClick"],
  buttonClassName?: string,
  title?: string,
  confirmText?: string,
  cancelText?: string,
  isClosingOnNextTick?: boolean,
  children?: ReactNode | ConfirmButtonRenderer
}

function ConfirmButton(props: ConfirmButtonProps) {
  const {
    small,
    stacked,
    iconSrc,
    className,
    buttonClassName,
    loading,
    intent = "danger",
    isText,
    title = "Vennligst bekreft",
    confirmText = "Ja",
    cancelText = "Nei",
    placement,
    isPortal,
    isClosingOnNextTick = false,
    disabled,
    onConfirm,
    onCancel,
    onClose,
    children,
    ...buttonProps
  } = props

  const [ shown, setShown ] = useState(false)

  const isMounted = useRef(false)
  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  })

  const close = () => {
    if(!isClosingOnNextTick) {
      setShown(false)
      if(onClose) onClose()
      return
    }
    // need to defer closing if renderered inside a DropdownMenu
    // if not, this will count as an outside click
    setTimeout(() => {
      if(!isMounted.current) return
      setShown(false)
      if(onClose) onClose()
    })
  }

  return (
    <Popover
      shown={shown}
      placement={placement}
      isPortal={isPortal}
      contentClassName="p-2"
      content={
        <div className={cn("text-center", className)}>
          {title}

          <div className="mt-1 pt-2 gap-x-1 flex items-center border-t border-bg0 dark:border-bg2">
            <Button
              small
              className="flex-1"
              onClick={(e) => {
                if(onCancel) onCancel(e)
                close()
              }}
            >
              {cancelText}
            </Button>

            <Button
              small
              className="flex-1"
              intent={intent}
              onClick={(e) => {
                if(onConfirm) onConfirm(e)
                close()
              }}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      }
    >
      {typeof children === "function"
        ? (children as ConfirmButtonRenderer)(shown, setShown)
        : <Button
            className={buttonClassName}
            iconSrc={iconSrc}
            intent={intent}
            loading={loading}
            disabled={disabled}
            small={small}
            stacked={stacked}
            isText={isText}
            onClick={() => {
              setShown(!shown)
            }}
            {...buttonProps}
          >
            {children}
          </Button>
      }
    </Popover>
  )
}

export default ConfirmButton
